'use client';

import { useCallback, useEffect, useState } from 'react';

// Lead distance for below-the-fold modules whose placeholder height can differ
// from the loaded content: activation (fetch, render, possible collapse) must
// resolve while the module is still below the viewport, so nothing visible
// shifts. Roughly two mobile viewports absorbs fetch latency at scroll speed.
export const BELOW_FOLD_ACTIVATION_ROOT_MARGIN = '1600px 0px';

type ViewportActivationOptions = {
  rootMargin?: string;
  idleFallbackMs?: number;
  disabled?: boolean;
};

type ViewportActivationReturn<T extends Element> = {
  ref: (node: T | null) => void;
  isActive: boolean;
  activate: () => void;
};

export function useViewportActivation<T extends Element>({
  rootMargin = '600px 0px',
  idleFallbackMs = 1500,
  disabled = false,
}: ViewportActivationOptions = {}): ViewportActivationReturn<T> {
  const [node, setNode] = useState<T | null>(null);
  const [isActive, setIsActive] = useState(false);

  const activate = useCallback(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (disabled || isActive) return;

    if (typeof window === 'undefined') return;

    if (!('IntersectionObserver' in window)) {
      const id = setTimeout(activate, idleFallbackMs);
      return () => clearTimeout(id);
    }

    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          activate();
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [activate, disabled, idleFallbackMs, isActive, node, rootMargin]);

  return { ref: setNode, isActive, activate };
}
